import { useEffect, useState } from "react";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { Trophy, Medal, Crown, Newspaper, Eye, Sparkles, ChevronLeft, ChevronRight, ShieldCheck } from "lucide-react";

const HINDI_MONTHS = ["", "जनवरी", "फरवरी", "मार्च", "अप्रैल", "मई", "जून",
                     "जुलाई", "अगस्त", "सितंबर", "अक्टूबर", "नवंबर", "दिसंबर"];

function rankMeta(rank) {
  if (rank === 1) return { icon: Crown, ring: "ring-amber-400", bg: "from-amber-400 via-yellow-500 to-amber-600", text: "प्रथम स्थान", trophy: "🥇" };
  if (rank === 2) return { icon: Medal, ring: "ring-slate-300", bg: "from-slate-300 via-slate-400 to-slate-500", text: "द्वितीय स्थान", trophy: "🥈" };
  if (rank === 3) return { icon: Medal, ring: "ring-orange-300", bg: "from-orange-400 via-orange-500 to-amber-700", text: "तृतीय स्थान", trophy: "🥉" };
  return { icon: Trophy, ring: "ring-slate-200", bg: "from-slate-100 to-slate-200", text: `${rank}वाँ स्थान`, trophy: "🏅" };
}

function PodiumCard({ leader }) {
  const m = rankMeta(leader.rank);
  const Icon = m.icon;
  return (
    <div className={`relative rounded-2xl overflow-hidden bg-gradient-to-br ${m.bg} text-white shadow-2xl p-5 ring-4 ${m.ring}`} data-testid={`podium-${leader.rank}`}>
      <div className="absolute top-3 right-3 text-3xl">{m.trophy}</div>
      <div className="flex flex-col items-center text-center">
        <div className="w-24 h-24 rounded-full ring-4 ring-white/60 bg-white/20 overflow-hidden mb-3 flex items-center justify-center">
          {leader.photo_url
            ? <img src={leader.photo_url} alt={leader.name} className="w-full h-full object-cover"/>
            : <span className="text-3xl font-black text-white">{(leader.name || "?").charAt(0)}</span>}
        </div>
        <div className="text-[10px] uppercase tracking-widest font-black opacity-90 inline-flex items-center gap-1"><Icon className="w-3 h-3"/>{m.text}</div>
        <h3 className="font-devnagri-heading text-xl font-black mt-1 line-clamp-2 leading-tight">{leader.name}</h3>
        <div className="text-[11px] mt-0.5 opacity-90">{leader.designation}{leader.district ? ` · ${leader.district}` : ""}</div>
        <div className="grid grid-cols-2 gap-2 w-full mt-4">
          <div className="bg-white/20 backdrop-blur rounded-lg py-2">
            <div className="text-2xl font-black tabular-nums leading-none">{leader.story_count}</div>
            <div className="text-[9px] uppercase tracking-widest font-bold opacity-90 mt-1">समाचार</div>
          </div>
          <div className="bg-white/20 backdrop-blur rounded-lg py-2">
            <div className="text-2xl font-black tabular-nums leading-none">{Number(leader.total_views || 0).toLocaleString("hi-IN")}</div>
            <div className="text-[9px] uppercase tracking-widest font-bold opacity-90 mt-1">पाठक</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ReporterRewardsPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [leaders, setLeaders] = useState([]);
  const [allTime, setAllTime] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get(`/reporter-rewards/leaderboard?year=${year}&month=${month}`),
        api.get(`/reporter-rewards/all-time?limit=10`),
      ]);
      setLeaders(a.data?.leaders || []);
      setAllTime(b.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const nav = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  const top3 = leaders.slice(0, 3);
  const others = leaders.slice(3);

  return (
    <>
      <PublicHeader />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #F59E0B 0, transparent 50%), radial-gradient(circle at 80% 70%, #DC2626 0, transparent 50%)" }}/>
        <div className="max-w-6xl mx-auto px-4 py-10 relative z-10">
          <div className="inline-flex items-center gap-2 bg-amber-400/20 text-amber-300 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest mb-3">
            <Sparkles className="w-3 h-3"/>Reporter Reward Wall
          </div>
          <h1 className="font-devnagri-heading text-3xl md:text-5xl font-black leading-tight">हमारे सम्मानित रिपोर्टर</h1>
          <p className="text-amber-200/90 mt-2 text-sm md:text-base max-w-3xl">हर महीने INDIAN-NEWS-21 के top रिपोर्टर को प्रधान संपादक <b>JAKIR MOMIN</b> द्वारा एक विशेष <b>Digital Certificate</b> प्रदान किया जाता है — pahchan, samman aur पत्रकारिता की प्रेरणा।</p>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4 py-8 pb-24" data-testid="reporter-rewards-page">
        {/* Month picker */}
        <div className="flex items-center justify-between gap-3 mb-6 rounded-xl bg-white border-2 border-slate-100 shadow-sm p-3">
          <button onClick={() => nav(-1)} className="w-10 h-10 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="rw-prev-month"><ChevronLeft className="w-5 h-5"/></button>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">इस माह का Hall of Fame</div>
            <div className="font-devnagri-heading text-xl font-black text-[#0F172A]">{HINDI_MONTHS[month]} {year}</div>
          </div>
          <button onClick={() => nav(1)} className="w-10 h-10 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="rw-next-month"><ChevronRight className="w-5 h-5"/></button>
        </div>

        {loading ? <div className="text-center py-12 text-slate-500">Loading…</div> :
         leaders.length === 0 ? (
          <div className="text-center py-16 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-2xl">
            <Trophy className="w-14 h-14 text-amber-500 mx-auto mb-3"/>
            <div className="font-devnagri-heading text-xl font-black text-[#0F172A]">इस माह अभी कोई रिपोर्ट प्रकाशित नहीं</div>
            <div className="text-sm text-slate-600 mt-2">जब रिपोर्टर खबरें प्रकाशित करेंगे, तब यहाँ leaderboard दिखेगा।</div>
          </div>
         ) : (
          <>
            {/* Podium — top 3 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {/* Show 2nd, 1st, 3rd order visually on md+ */}
              {top3[1] && <div className="md:order-1"><PodiumCard leader={top3[1]}/></div>}
              {top3[0] && <div className="md:order-2 md:-mt-4"><PodiumCard leader={top3[0]}/></div>}
              {top3[2] && <div className="md:order-3"><PodiumCard leader={top3[2]}/></div>}
            </div>

            {/* Others */}
            {others.length > 0 && (
              <div className="bg-white border-2 border-slate-100 rounded-xl shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                  <div className="text-[10px] uppercase tracking-widest font-black text-slate-500">Runner-Ups · {HINDI_MONTHS[month]} {year}</div>
                </div>
                <ul className="divide-y divide-slate-100">
                  {others.map((l) => (
                    <li key={l.staff_id} className="flex items-center gap-3 px-4 py-3" data-testid={`rw-row-${l.rank}`}>
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center font-black text-sm text-slate-600">{l.rank}</div>
                      <div className="w-10 h-10 rounded-full bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
                        {l.photo_url ? <img src={l.photo_url} alt={l.name} className="w-full h-full object-cover"/> : <span className="text-sm font-black text-slate-500">{(l.name || "?").charAt(0)}</span>}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-[#0F172A] truncate">{l.name}</div>
                        <div className="text-[11px] text-slate-500 truncate">{l.designation}{l.district ? ` · ${l.district}` : ""}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="flex items-center gap-3 text-xs">
                          <span className="inline-flex items-center gap-1 text-red-600 font-bold"><Newspaper className="w-3 h-3"/>{l.story_count}</span>
                          <span className="inline-flex items-center gap-1 text-sky-600 font-bold"><Eye className="w-3 h-3"/>{Number(l.total_views).toLocaleString("hi-IN")}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
         )}

        {/* All-time */}
        <div className="mt-10 rounded-2xl overflow-hidden bg-gradient-to-br from-slate-900 to-[#0F172A] text-white">
          <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-amber-400"/>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">Hall of Legends</div>
              <div className="font-devnagri-heading text-lg font-black">सर्वकालीन शीर्ष रिपोर्टर</div>
            </div>
          </div>
          {allTime.length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-sm">जल्द ही यहाँ हमारे सबसे उत्कृष्ट रिपोर्टर दिखेंगे।</div>
          ) : (
            <ul className="divide-y divide-white/5">
              {allTime.map((l) => (
                <li key={l.staff_id} className="flex items-center gap-3 px-5 py-3">
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center font-black text-sm text-amber-300">{l.rank}</div>
                  <div className="w-10 h-10 rounded-full bg-white/10 overflow-hidden shrink-0 flex items-center justify-center">
                    {l.photo_url ? <img src={l.photo_url} alt={l.name} className="w-full h-full object-cover"/> : <span className="text-sm font-black">{(l.name || "?").charAt(0)}</span>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold truncate">{l.name}</div>
                    <div className="text-[11px] text-slate-400 truncate">{l.designation}{l.district ? ` · ${l.district}` : ""}</div>
                  </div>
                  <div className="text-xs flex items-center gap-3">
                    <span className="inline-flex items-center gap-1 text-red-300 font-bold"><Newspaper className="w-3 h-3"/>{l.story_count}</span>
                    <span className="inline-flex items-center gap-1 text-sky-300 font-bold"><Eye className="w-3 h-3"/>{Number(l.total_views).toLocaleString("hi-IN")}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
      <PublicFooter />
    </>
  );
}
